<template>
  <div class="app-calendar overflow-hidden border">
    <div class="row no-gutters">
      <!-- Sidebar -->
      <div
        class="col app-calendar-sidebar flex-grow-0 overflow-hidden d-flex flex-column"
        :class="{ show: isCalendarOverlaySidebarActive }"
      >
        <calendar-sidebar
          :is-event-handler-sidebar-active.sync="isEventHandlerSidebarActive"
          :is-calendar-overlay-sidebar-active.sync="isCalendarOverlaySidebarActive"
          @add-event="() => { clearEventData(); isEventHandlerSidebarActive = true; }"
        />
      </div>

      <!-- Calendar -->
    <!-- Full Calendar Card -->
    <div class="col position-relative">
      <div class="card shadow-none border-0 mb-0 rounded-0 overflow-hidden">
        <!-- Skeleton Grid Overlay -->
        <transition name="fade">
          <div v-if="isCalendarBusy" class="calendar-skeleton-overlay">
            <div class="skeleton-header d-flex justify-content-between mb-3 px-2 pt-2">
              <div class="skeleton-title-box"></div>
              <div class="skeleton-nav-box d-flex">
                <div class="skeleton-btn mr-1"></div>
                <div class="skeleton-btn"></div>
              </div>
            </div>
            <div class="skeleton-grid px-2 pb-2">
              <div class="skeleton-row-header d-flex mb-2">
                <div v-for="d in 7" :key="'sh-'+d" class="skeleton-cell-header"></div>
              </div>
              <div v-for="r in 6" :key="'sr-'+r" class="skeleton-row d-flex">
                <div v-for="c in 7" :key="'sc-'+r+'-'+c" class="skeleton-cell">
                  <div class="skeleton-date"></div>
                  <div v-if="c%3==0" class="skeleton-event-bar mb-1"></div>
                  <div v-if="c%2==0" class="skeleton-event-bar-small"></div>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <div class="card-body pb-0" :class="{ 'calendar-loading-blur': isCalendarBusy }">
          <full-calendar
            ref="refCalendar"
            :options="calendarOptions"
            class="full-calendar"
          >
            <template v-slot:eventContent="arg">
              <div class="event-content d-flex flex-column">
                <b class="event-title" :title="arg.event.title">{{ truncate(arg.event.title, 90) }}</b>
                <!-- Event Type Badge -->
                <small 
                  v-if="arg.event.extendedProps.eventType"
                  class="event-type-badge"
                  :class="{
                    'badge-event': arg.event.extendedProps.eventType === 'event',
                    'badge-holiday': arg.event.extendedProps.eventType === 'holiday'
                  }"
                >
                  {{ arg.event.extendedProps.eventType === 'holiday' ? 'Holiday' : 'Event' }}
                </small>
              </div>
            </template>
          </full-calendar>
        </div>
      </div>
    </div>

      <!-- Sidebar Overlay -->
      <div
        class="body-content-overlay"
        :class="{ show: isCalendarOverlaySidebarActive }"
        @click="isCalendarOverlaySidebarActive = false"
      />
      <calendar-event-handler
        v-model="isEventHandlerSidebarActive"
        :event="event"
        :clear-event-data="clearEventData"
        @remove-event="removeEvent"
        @add-event="addEvent"
        @update-event="updateEvent"
        @refetch-events="refetchEvents"
      />
    </div>
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import store from "@/store";
import { onUnmounted } from "@vue/composition-api";
import calendarStoreModule from "./calendarStoreModule";
import CalendarSidebar from "./calendar-sidebar/CalendarSidebar.vue";
import CalendarEventHandler from "./calendar-event-handler/CalendarEventHandler.vue";
import useCalendar from "./useCalendar";
import useCalendarSidebar from "./calendar-sidebar/useCalendarSidebar";

export default {
  components: {
    FullCalendar, // make the <FullCalendar> tag available
    CalendarSidebar,
    CalendarEventHandler,
  },
  methods: {
    truncate(text, max = 90) {
      if (!text) return "";
      const t = String(text).trim();
      if (t.length <= max) return t;
      return t.slice(0, Math.max(0, max - 1)) + "…";
    },
  },
  setup() {
    const CALENDAR_APP_STORE_MODULE_NAME = "calendar";

    // Register module
    if (!store.hasModule(CALENDAR_APP_STORE_MODULE_NAME))
      store.registerModule(CALENDAR_APP_STORE_MODULE_NAME, calendarStoreModule);

    // Reset filters on load (user requested not to save selections across navigation)
    const { checkAll } = useCalendarSidebar();
    checkAll.value = true;

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(CALENDAR_APP_STORE_MODULE_NAME))
        store.unregisterModule(CALENDAR_APP_STORE_MODULE_NAME);
    });

    const {
      refCalendar,
      isCalendarOverlaySidebarActive,
      event,
      clearEventData,
      addEvent,
      updateEvent,
      removeEvent,
      refetchEvents,
      calendarOptions,
      isCalendarBusy,

      // ----- UI ----- //
      isEventHandlerSidebarActive,
    } = useCalendar();

    return {
      refCalendar,
      isCalendarOverlaySidebarActive,
      event,
      clearEventData,
      addEvent,
      updateEvent,
      removeEvent,
      refetchEvents,
      calendarOptions,
      isCalendarBusy,

      // ----- UI ----- //
      isEventHandlerSidebarActive,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/apps/calendar.scss";

// --- CALENDAR SKELETON & LOADING ---

.calendar-skeleton-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  z-index: 50;
  display: flex;
  flex-direction: column;

  .skeleton-header {
    height: 70px;
    align-items: center;
  }

  .skeleton-title-box {
    width: 200px;
    height: 32px;
    background: #f1f5f9;
    border-radius: 8px;
    animation: shimmer 1.5s infinite linear;
  }

  .skeleton-btn {
    width: 40px;
    height: 40px;
    background: #f1f5f9;
    border-radius: 12px;
    animation: shimmer 1.5s infinite linear;
  }

  .skeleton-cell-header {
    flex: 1;
    height: 20px;
    background: #f8fafc;
    margin: 0 4px;
    border-radius: 4px;
    animation: shimmer 1.5s infinite linear;
  }

  .skeleton-cell {
    flex: 1;
    height: 110px;
    border: 1px solid #f8fafc;
    padding: 10px;
    background: #fff;

    .skeleton-date {
      width: 24px;
      height: 24px;
      background: #f1f5f9;
      border-radius: 6px;
      margin-bottom: 20px;
      margin-left: auto;
      animation: shimmer 1.5s infinite linear;
    }

    .skeleton-event-bar {
      height: 40px;
      background: #f1f5f9;
      border-radius: 10px;
      width: 100%;
      animation: shimmer 1.5s infinite linear;
    }

    .skeleton-event-bar-small {
      height: 35px;
      background: #f8fafc;
      border-radius: 10px;
      width: 85%;
      animation: shimmer 1.5s infinite linear;
    }
  }
}

.calendar-loading-blur {
  filter: blur(2px);
  opacity: 0.6;
  pointer-events: none;
  transition: all 0.3s ease;
}

@keyframes shimmer {
  0% { background: #f1f5f9; }
  50% { background: #e2e8f0; }
  100% { background: #f1f5f9; }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

// --- SIDEBAR TOGGLE ICON ---
.fc-sidebarToggle-button {
  padding: 0 !important;
  width: 40px !important;
  height: 40px !important;
  display: none !important; // Hidden on desktop
  align-items: center;
  justify-content: center;
  background: #f8fafc !important;
  border: 1px solid #f1f5f9 !important;
  position: relative;
  
  @media (max-width: 991px) {
    display: flex !important;
  }

  &::after {
    content: "";
    display: block;
    width: 18px;
    height: 18px;
    background-color: #7367f0;
    mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cline x1='3' y1='12' x2='21' y2='12'%3E%3C/line%3E%3Cline x1='3' y1='6' x2='21' y2='6'%3E%3C/line%3E%3Cline x1='3' y1='18' x2='21' y2='18'%3E%3C/line%3E%3C/svg%3E");
    mask-repeat: no-repeat;
    mask-position: center;
    mask-size: contain;
  }

  &:hover {
    background: #fff !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
  }
}

// --- TOP TIER CORPORATE CALENDAR THEME ---

.app-calendar {
  border-radius: 20px !important;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.04);
  background: #fff;
  border: 1px solid #f1f5f9 !important;
  overflow: hidden;
  
  .app-calendar-sidebar {
    background: #ffffff;
    border-right: 1px solid #f1f5f9;
    padding: 0;
    min-width: 280px;
  }
}

.full-calendar {
  padding: 1.5rem !important;

  // Header Toolbar
  .fc-header-toolbar {
    margin-bottom: 2.5rem !important;
    align-items: center;
    
    .fc-toolbar-title {
      font-size: 1.75rem !important;
      font-weight: 800 !important;
      color: #1e293b !important;
      letter-spacing: -0.5px;
      margin-left: 1rem;
    }

    // Navigation Buttons
    .fc-button-group {
      background: #f8fafc;
      padding: 4px;
      border-radius: 12px;
      border: 1px solid #f1f5f9;

      .fc-button {
        background: transparent !important;
        border: none !important;
        color: #64748b !important;
        padding: 0.5rem 1rem !important;
        font-weight: 700 !important;
        font-size: 0.85rem !important;
        border-radius: 10px !important;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        text-transform: capitalize;

        &:hover {
          color: #1e293b !important;
          background: #fff !important;
        }

        &.fc-button-active {
          background: #ffffff !important;
          color: #7367f0 !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
        }
      }
    }

    .fc-prev-button, .fc-next-button {
      background: #fff !important;
      border: 1px solid #f1f5f9 !important;
      color: #64748b !important;
      border-radius: 50% !important;
      padding: 0.4rem !important;
      margin: 0 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);

      &:hover {
        border-color: #cbd5e1 !important;
        color: #1e293b !important;
        transform: translateY(-1px);
      }
    }
  }

  // Grid Lines & Header
  .fc-theme-standard {
    td, th {
      border: 1px solid #f1f5f9 !important;
    }
    
    .fc-col-header-cell {
      padding: 1.25rem 0;
      background: #fff;
      border-bottom: 2px solid #f1f5f9 !important;

      .fc-col-header-cell-cushion {
        color: #94a3b8;
        font-weight: 800;
        text-transform: uppercase;
        font-size: 0.7rem;
        letter-spacing: 1.5px;
      }
    }
  }

  // Single Day Cell
  .fc-daygrid-day {
    min-height: 120px !important;

    &:hover {
      background: #fcfcfd !important;
    }

    &.fc-day-today {
      background: #f8faff !important;

      .fc-daygrid-day-number {
        background: linear-gradient(135deg, #7367f0, #9d7aff);
        color: #fff !important;
        box-shadow: 0 4px 10px rgba(115, 103, 240, 0.35);
      }
    }

    .fc-daygrid-day-top {
      padding: 10px;
    }

    .fc-daygrid-day-number {
      font-weight: 700;
      color: #64748b;
      font-size: 0.95rem;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 10px;
      transition: all 0.2s;
    }
  }

  // --- PREMIUM EVENT DISPLAY ---
  .fc-event {
    background: #fff !important;
    border: none !important;
    margin: 4px 8px !important;
    padding: 0 !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    overflow: hidden;

    &:hover {
      transform: translateY(-3px) scale(1.01);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
      z-index: 100 !important;
    }
  }

  .fc-daygrid-event-h-harness {
    margin-bottom: 6px;
  }
}

// Inner Event Content Styling
.event-content {
  padding: 8px 10px;
  border-left: 4px solid #ddd;
  min-height: 52px;
  display: flex !important;
  flex-direction: column;
  justify-content: center;

  .event-title {
    font-size: 0.85rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 2px;
  }

  .event-type-badge {
    font-size: 0.62rem;
    font-weight: 800;
    padding: 2px 8px;
    border-radius: 20px;
    display: inline-flex;
    align-self: flex-start;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 2px;
    
    &.badge-event {
      background: #f0f1ff;
      color: #7367f0;
    }
    
    &.badge-holiday {
      background: #fff3e0;
      color: #ff9f43;
    }
  }
}

// Map backgrounds from parent if needed or keep clean white
.fc-event-students .event-content { border-left-color: #7367f0; background: rgba(115, 103, 240, 0.02); }
.fc-event-staff .event-content { border-left-color: #ea5455; background: rgba(234, 84, 85, 0.02); }
.fc-event-both .event-content { border-left-color: #00cfe8; background: rgba(0, 207, 232, 0.02); }
.fc-event-default .event-content { border-left-color: #28c76f; background: rgba(40, 199, 111, 0.02); }

</style>


